import {
  Notification,
  ProgressIndicator,
} from "@ftdr/blueprint-components-react";
import IframeResizer from "iframe-resizer-react";
import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import {
  AgregatedProcessPaymentResponse,
  MESSAGE_ACTION,
  PaymentProcessorClientInternalRef,
  ProcessPaymentPaymentusRequestData,
} from "../types";
import { useIframeInitialization, useTokenSemanticValidation } from "./hooks";
import { ApplicationStateType } from "../../payments-component";
import { setIsPending } from "../../../contexts/payment-context/state";
import { getSaveInWallet } from "../../../utils";
import { progressIndicatorColorMap } from "../../../const";

export type PromiseRefType = {
  resolve?: (result: AgregatedProcessPaymentResponse | undefined) => void;
};

export type PaymentusIframeProps = { applicationState: ApplicationStateType };

export const PaymentusIframe = forwardRef<
  Pick<
    PaymentProcessorClientInternalRef<ProcessPaymentPaymentusRequestData>,
    "processPayment"
  >,
  PaymentusIframeProps
>(({ applicationState }, ref) => {
  const {
    brand,
    subbrand,
    dispatch,
    token,
    paymentusIframeBaseUrl,
    applicationName,
    owner,
    formsSetup,
    googleAnalytics,
    styleType,
    state: { isSetAsDefaultChecked, isSaveInWalletChecked },
    setWalletCheckboxProps,
  } = applicationState;
  const {
    iframeRef,
    isIframeReady,
    onMessageReceivedFromIframe,
    onInitIframeHandler,
    promise,
    isSetAsDefaultCheckedRef,
  } = useIframeInitialization();

  const tokenSemanticValidResult = useTokenSemanticValidation(token);

  useImperativeHandle(ref, () => ({
    processPayment: async (requestData) => {
      dispatch(setIsPending(true));
      iframeRef?.current!.sendMessage({
        action: MESSAGE_ACTION.SUBMIT,
        data: {
          submit_data: {
            paymentus: {
              brand: brand,
              ...(subbrand ? { subbrand } : {}),
              source: {
                name: requestData?.paymentus?.source?.name || applicationName,
              },
              owner: owner,
              ...requestData?.paymentus,
              includedInWallet: getSaveInWallet(
                isSetAsDefaultChecked,
                isSaveInWalletChecked,
                !!requestData?.paymentus?.includedInWallet
              ),
            },
          },
        },
      });
      return new Promise<AgregatedProcessPaymentResponse | undefined>(
        (resolve) => {
          promise.current!.resolve = resolve;
        }
      );
    },
  }));

  useEffect(() => {
    if (isIframeReady) {
      iframeRef?.current!.sendMessage({
        action: MESSAGE_ACTION.PAYMENTUS_FORM_CONFIG,
        data: {
          paymentus_form_config: formsSetup?.paymentusForm,
        },
      });
    }
  }, [
    formsSetup?.paymentusForm,
    isIframeReady,
    iframeRef,
    isSetAsDefaultChecked,
    setWalletCheckboxProps?.showWalletCheckbox,
  ]);

  useEffect(() => {
    isSetAsDefaultCheckedRef.current = isSetAsDefaultChecked;
  }, [isSetAsDefaultChecked, isSetAsDefaultCheckedRef]);

  return (
    <div>
      {tokenSemanticValidResult.isError ? (
        <Notification
          variant="inline"
          status="error"
          size="medium"
          showStatusLabel={true}
          inline={true}
          data-testid="paymentus-iframe-token-error-notification"
        >
          <>{tokenSemanticValidResult.message}</>
        </Notification>
      ) : (
        <div className="flex justify-center flex-col">
          {!isIframeReady && (
            <ProgressIndicator
              className="my-4"
              size="medium"
              color={progressIndicatorColorMap[styleType || "default"]}
            />
          )}
          <IframeResizer
            forwardRef={iframeRef}
            onMessage={onMessageReceivedFromIframe}
            onInit={onInitIframeHandler}
            src={`${paymentusIframeBaseUrl}/?token=${token}&analytics=${googleAnalytics}&theme=${styleType}`}
            checkOrigin={false}
            style={{
              width: "1px",
              minWidth: "100%",
              ...(!isIframeReady && { display: "none" }),
            }}
            data-testid="paymentus-iframe-resizer"
          />
        </div>
      )}
    </div>
  );
});

PaymentusIframe.displayName = "PaymentusIframe";
